import { useId, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import { useOrganizationApi } from "@/hooks/apiHooks/useOrganizationApi";
import { useSubmitGuard } from "@/hooks/useSubmitGuard";
import { AdminDrawer } from "@admin/_components/AdminDrawer";
import { Alert } from "@common/Alert";
import { Button } from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import { ROUTES } from "@routing/routes";
import type { OrganizationDetail } from "@models/organization";
import {
    editOrganizationRefusal,
    type EditOrganizationRefusal,
} from "../_helpers/editOrganization";
import {
    organizationFormSchema,
    toOrganizationFormValues,
    toUpdateOrganizationRequest,
    type OrganizationFormValues,
} from "../_helpers/organizationForm";
import { OrganizationFormFields } from "./OrganizationFormFields";

export interface EditOrganizationSaved {
    // The API's answer: null when no new signup link was issued (or the 200
    // carried no answer), else whether its email went out.
    invitationSent: boolean | null;
    // The contact email as the API stores it (trimmed and lower-case), where
    // a new signup link went.
    email: string;
}

interface EditOrganizationDrawerProps {
    // The page's detail when the drawer opened: the form is filled from it and
    // the save carries its updatedAt.
    organization: OrganizationDetail;
    onClose: () => void;
    // A 200: the page closes the drawer and re-reads the client.
    onSaved: (saved: EditOrganizationSaved) => void;
    // The API's 404: the client is gone. The page closes the drawer and says
    // so, as it does for a status change's 404.
    onGone: () => void;
    // Reads the client again for "Load latest"; null when that read failed.
    loadLatest: () => Promise<OrganizationDetail | null>;
}

// C5 (API #426): a platform admin edits the client's organization. A refusal
// keeps the drawer open with the admin's edits and says why; a stale copy
// (organization_changed) offers the latest details instead of saving over them.
// A client the API says is gone leaves nothing to edit: the page takes over.
export const EditOrganizationDrawer = ({
    organization,
    onClose,
    onSaved,
    onGone,
    loadLatest,
}: EditOrganizationDrawerProps) => {
    const { updateOrganization } = useOrganizationApi();
    const runGuarded = useSubmitGuard();
    const formId = useId();

    // The read the form was filled from, whose updatedAt the save sends back
    // verbatim. Replaced only by "Load latest".
    const [readAs, setReadAs] = useState(organization);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingLatest, setIsLoadingLatest] = useState(false);
    const [refusal, setRefusal] = useState<EditOrganizationRefusal | null>(
        null,
    );
    const [latestFailed, setLatestFailed] = useState(false);
    const [showsLatest, setShowsLatest] = useState(false);

    const methods = useForm<OrganizationFormValues>({
        resolver: zodResolver(organizationFormSchema),
        defaultValues: toOrganizationFormValues(organization),
    });

    const isProcessing = isSaving || isLoadingLatest;

    const save = (values: OrganizationFormValues) =>
        runGuarded(async () => {
            setRefusal(null);
            setLatestFailed(false);
            setShowsLatest(false);
            setIsSaving(true);

            try {
                const response = await updateOrganization(
                    readAs.id,
                    toUpdateOrganizationRequest(values, readAs),
                );
                onSaved({
                    invitationSent: response?.invitationSent ?? null,
                    // Already trimmed by the form's rule.
                    email: values.email.toLowerCase(),
                });
            } catch (error) {
                const refused = editOrganizationRefusal(error);
                if (refused.kind === "gone") {
                    onGone();
                    return;
                }
                setRefusal(refused);
            } finally {
                setIsSaving(false);
            }
        });

    const showLatest = () =>
        runGuarded(async () => {
            setLatestFailed(false);
            setIsLoadingLatest(true);

            try {
                const latest = await loadLatest();
                if (!latest) {
                    setLatestFailed(true);
                    return;
                }
                setReadAs(latest);
                methods.reset(toOrganizationFormValues(latest));
                setRefusal(null);
                setShowsLatest(true);
            } finally {
                setIsLoadingLatest(false);
            }
        });

    return (
        <AdminDrawer
            title="Edit Organization"
            onClose={onClose}
            isProcessing={isProcessing}
            footer={
                <div className="flex items-center gap-4">
                    {/* Outside the form, so tied to it by `form`. A native
                        button: Common Button does not forward `form`. */}
                    <button
                        type="submit"
                        form={formId}
                        disabled={isProcessing}
                        className="btn btn-primary"
                    >
                        {isSaving && (
                            <span className="loading loading-spinner" />
                        )}
                        {isSaving ? "Saving…" : "Save Changes"}
                    </button>
                    {/* text-neutral: a ghost button on a light surface is
                        otherwise white on white (see the UI CLAUDE.md). */}
                    <Button
                        color={ComponentColor.Ghost}
                        className="text-neutral"
                        disabled={isProcessing}
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                </div>
            }
        >
            {refusal && (
                <Alert color={ComponentColor.Error} className="mb-4">
                    <p>{refusal.message}</p>
                    {refusal.kind === "email_in_use" &&
                        refusal.existingOrganizationId && (
                        <Link
                            to={ROUTES.ADMIN_CLIENTS_DETAIL(
                                refusal.existingOrganizationId,
                            )}
                            className="link mt-2 inline-block font-semibold"
                        >
                            Open that client
                        </Link>
                    )}
                    {refusal.kind === "organization_changed" && (
                        <>
                            {latestFailed && (
                                <p className="mt-2">
                                    The latest details couldn&apos;t be loaded.
                                    Try again.
                                </p>
                            )}
                            <Button
                                size={ComponentSize.SM}
                                outline
                                className="mt-2"
                                disabled={isProcessing}
                                onClick={() => void showLatest()}
                            >
                                {isLoadingLatest ? "Loading…" : "Load latest"}
                            </Button>
                        </>
                    )}
                </Alert>
            )}
            {showsLatest && (
                <p role="status" className="mb-4 text-sm font-medium">
                    These are the latest details. Make your changes again.
                </p>
            )}
            <FormProvider {...methods}>
                <form
                    id={formId}
                    noValidate
                    onSubmit={methods.handleSubmit((values) => save(values))}
                >
                    <fieldset disabled={isProcessing}>
                        <OrganizationFormFields />
                    </fieldset>
                </form>
            </FormProvider>
        </AdminDrawer>
    );
};
